// ink-component
import React, { MouseEvent, RefObject } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import useInkAnalytics from '../../hooks/useInkAnalytics';

import { favoriteTokens } from '../../tokens/components/Favorite/tokens';
import { variants } from './styles';

export interface FavoriteProps {
  /** Display if it's checked */
  isFavorited?: boolean;
  /** Disabled state */
  isDisabled?: boolean;
  /** Onclick Callback */
  onClick?: (event: MouseEvent) => void;
  /** Button color */
  color?: 'black' | 'white' | 'gold' | 'inherit';
  /** React ref object to be forwarded to the internal node */
  externalRef?: RefObject<HTMLOrSVGElement>;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
  'data-track-name'?: string;
  /** Custom payload to be sent to analytics provider */
  'data-track-payload'?: any;
  /** List of tracked events in the analytics integration */
  'data-track-events'?: string[];
}

const StyledFavorite = styled.svg`
  cursor: pointer;
  position: relative;
  top: 1px;
  stroke: ${({ theme }) => variants[theme.favoriteColor].stroke};
  fill: ${({ theme }) => variants[theme.favoriteColor].initial};

  ${({ theme }) =>
    theme.favorited &&
    css`
      fill: ${({ theme }) => variants[theme.favoriteColor].selected};
    `};

  &:focus {
    outline: none;
  }

  // Everything not disabled
  ${({ theme }) =>
    !theme.favoriteDisabled &&
    css`
      &:focus-visible {
        outline-width: ${favoriteTokens.OutlineWidth};
        outline-offset: ${favoriteTokens.OutlineOffset};
        outline-style: ${favoriteTokens.OutlineStyle};
        outline-color: ${({ theme }) => variants[theme.favoriteColor].outline};
      }

      &:hover {
        fill: ${({ theme }) => variants[theme.favoriteColor].hover};
      }

      &:active {
        fill: ${({ theme }) => variants[theme.favoriteColor].pressed};
      }
    `}

  ${({ theme }) =>
    theme.favoriteDisabled &&
    css`
      cursor: default;
      fill: ${({ theme }) => variants[theme.favoriteColor].disabled};
      stroke: ${({ theme }) => variants[theme.favoriteColor].disabled};
    `};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose A clickable star indicating a "favorite" option
 * @categories accessory
 * @aka star
 */
function Favorite(props: FavoriteProps) {
  const { isFavorited, isDisabled, onClick, color, externalRef } = props;

  const [ref] = useInkAnalytics(props, { ref: externalRef });

  const onKeyDown = e => {
    if (!isDisabled && (e.key === 'Enter' || e.keyCode === 13)) {
      onClick && onClick(e);
    }
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        favoriteDisabled: isDisabled,
        favoriteColor: color,
        favorited: isFavorited,
      })}
    >
      <StyledFavorite
        {...globalProps}
        ref={ref}
        role="button"
        aria-label="favorite"
        tabIndex={isDisabled ? -1 : 0}
        viewBox="0 0 217 206"
        width="14"
        strokeWidth="10"
        onClick={e => !isDisabled && onClick && onClick(e)}
        onKeyDown={onKeyDown}
        data-testid={props['data-testid']}
      >
        <path d="M108.5-.3l33.4 77.8 74.8.8-57.1 52.8 15.8 74.5-66.9-35.2-66.9 35.2 15.8-74.5L.3 78.3l74.8-.8z" />
      </StyledFavorite>
    </ThemeProvider>
  );
}

Favorite.defaultProps = {
  isFavorited: false,
  isDisabled: false,
  color: 'gold',
  'data-track-events': ['click'],
};

Favorite.displayName = 'Favorite';

export default Favorite;
